import api from './api';
import { normalizeDialCode } from '../utils/phoneDialCode';
import { parsePhoneLength } from '../utils/personFieldValidation';

export interface CountryDialOption {
  code: string;
  name: string;
  dialCode: string;
  phoneLength?: string | number;
}

/** Used when country masters /app/countries are empty or unavailable. */
export const FALLBACK_COUNTRY_DIAL_CODES: CountryDialOption[] = [
  { code: 'IN', name: 'India', dialCode: '+91', phoneLength: 10 },
  { code: 'US', name: 'United States', dialCode: '+1', phoneLength: 10 },
  { code: 'GB', name: 'United Kingdom', dialCode: '+44', phoneLength: '10-11' },
  { code: 'AE', name: 'United Arab Emirates', dialCode: '+971', phoneLength: 9 },
  { code: 'SG', name: 'Singapore', dialCode: '+65', phoneLength: 8 },
  { code: 'AU', name: 'Australia', dialCode: '+61', phoneLength: 9 },
  { code: 'CA', name: 'Canada', dialCode: '+1', phoneLength: 10 },
  { code: 'DE', name: 'Germany', dialCode: '+49', phoneLength: '10-11' },
  { code: 'FR', name: 'France', dialCode: '+33', phoneLength: 9 },
  { code: 'JP', name: 'Japan', dialCode: '+81', phoneLength: '10-11' },
];

function extractRows(payload: unknown): Record<string, unknown>[] {
  if (Array.isArray(payload)) return payload as Record<string, unknown>[];
  if (!payload || typeof payload !== 'object') return [];
  const obj = payload as Record<string, unknown>;
  if (Array.isArray(obj.data)) return obj.data as Record<string, unknown>[];
  if (Array.isArray(obj.rows)) return obj.rows as Record<string, unknown>[];
  if (Array.isArray(obj.countries)) return obj.countries as Record<string, unknown>[];
  return [];
}

function fromCountryRow(row: Record<string, unknown>): CountryDialOption | null {
  const meta = (row.metadata && typeof row.metadata === 'object' ? row.metadata : {}) as Record<string, unknown>;
  const dialCode = normalizeDialCode(String(
    row.dial_code || row.dialCode || row.phoneCode || meta.dial_code || meta.dialCode || meta.phoneCode || '',
  ));
  if (!dialCode) return null;
  const code = String(row.code || row.country_code || row.countryCode || meta.country_code || '').toUpperCase();
  const name = String(row.name || row.full_name || row.fullName || code || dialCode);
  const phoneLength = row.phoneLength ?? row.phone_number_length ?? meta.phoneLength ?? meta.phone_number_length;
  return { code, name, dialCode, phoneLength: phoneLength as string | number | undefined };
}

function isInactive(row: Record<string, unknown>): boolean {
  if (row.status === false || row.status === 'false') return true;
  if (row.isActive === false) return true;
  return false;
}

function dedupe(options: CountryDialOption[]): CountryDialOption[] {
  const seen = new Set<string>();
  const out: CountryDialOption[] = [];
  for (const opt of options) {
    const key = `${opt.dialCode}|${opt.code}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(opt);
  }
  return out.sort((a, b) => a.name.localeCompare(b.name));
}

function mapRows(rows: Record<string, unknown>[]): CountryDialOption[] {
  const mapped = rows.map(fromCountryRow).filter(Boolean) as CountryDialOption[];
  const activeRows = rows.filter((r) => !isInactive(r));
  const active = activeRows.map(fromCountryRow).filter(Boolean) as CountryDialOption[];
  return dedupe(active.length ? active : mapped);
}

let cachedDialCodes: CountryDialOption[] = [...FALLBACK_COUNTRY_DIAL_CODES];

export function getCachedCountryDialCodes(): CountryDialOption[] {
  return cachedDialCodes.length ? cachedDialCodes : FALLBACK_COUNTRY_DIAL_CODES;
}

export function phoneLengthForDialCode(dialCode: string): { min: number; max: number } | null {
  const normalized = normalizeDialCode(dialCode);
  if (!normalized) return null;
  const list = getCachedCountryDialCodes();
  const match = list.find((o) => o.dialCode === normalized);
  const fromMatch = parsePhoneLength(match?.phoneLength);
  if (fromMatch) return fromMatch;
  const fallback = FALLBACK_COUNTRY_DIAL_CODES.find((o) => o.dialCode === normalized);
  return parsePhoneLength(fallback?.phoneLength);
}

function withFallbackLengths(rows: CountryDialOption[]): CountryDialOption[] {
  return rows.map((row) => {
    if (parsePhoneLength(row.phoneLength)) return row;
    const fb = FALLBACK_COUNTRY_DIAL_CODES.find(
      (o) => o.dialCode === row.dialCode || (row.code && o.code === row.code),
    );
    if (!fb?.phoneLength) return row;
    return { ...row, phoneLength: fb.phoneLength };
  });
}

async function fetchDialCodeSource(url: string, params?: Record<string, unknown>): Promise<CountryDialOption[]> {
  const res = await api.get(url, { params });
  return withFallbackLengths(mapRows(extractRows(res.data?.data ?? res.data)));
}

export async function listCountryDialCodes(): Promise<CountryDialOption[]> {
  const sources = [
    () => fetchDialCodeSource('/app/countries'),
    () => fetchDialCodeSource('/admin/masters/transport_countries', { limit: 500 }),
    () => fetchDialCodeSource('/admin/masters/countries', { limit: 500 }),
  ];

  for (const load of sources) {
    try {
      const rows = await load();
      if (rows.length) {
        cachedDialCodes = rows;
        return rows;
      }
    } catch {
      /* try next backend source */
    }
  }

  cachedDialCodes = [...FALLBACK_COUNTRY_DIAL_CODES];
  return cachedDialCodes;
}
